import React, { useContext, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { TimeRangeContext } from '../../context/TimeRangeContext';
import { useEventTypes } from '../../context/EventTypeContext';
import ActivityApi from '../../api/instance/ActivityApi';
import EventsApi from '../../api/instance/EventsApi';

// Helpers
import Helper from '../../helpers/Helper';

// Types
import { InstanceDetailSlideoutProps } from './types';
import CombinedChanges from '../../types/instance/CombinedChanges';
import Statement from '../../types/instance/Statement';
import Wait from '../../types/instance/Wait';
import ChartPlotLine from '../../types/ChartPlotLine';

// Components
import ProcessorChart from '../../component/host/chart/ProcessorChart';
import WidgetCard from "../../component/instance/widget";
import TimeChart from '../../component/instance/chart/TimeChart';
import StatusIcon from '../../component/StatusIcon';
import InstanceAgBadge from '../../component/cluster/InstanceAgBadge';

// Constants
import { CHART_DATA_OPTION, chartOptionsList, CHART_COLOURS_WAITS, CHART_COLOURS_STATEMENTS } from '../../component/Constants';
import { API_DATE_FORMAT, buildRedirectQuery, buildApiQuery } from '../../helpers/timeRangeUtils';
import DiskChart from "../../component/host/chart/DiskChart";

const InstanceDetailSlideout: React.FC<InstanceDetailSlideoutProps> = ({ instance: instanceDetails, hostDetails, onClose}) => {
    const timeRangeContext = useContext(TimeRangeContext);
    const { timeRange } = timeRangeContext;
    const { eventTypes } = useEventTypes();
    const [statements, setStatements] = useState<Statement[]>([]);
    const [waits, setWaits] = useState<Wait[]>([]);
    const [clients, setClients] = useState([]);
    const [programs, setPrograms] = useState([]);
    const [users, setUsers] = useState([]);
    const [sessions, setSessions] = useState([]);
    const [databases, setDatabases] = useState([]);
    const [plotLines, setPlotLines] = useState<ChartPlotLine[]>([]);
    const [selectedDataOption, setSelectedDataOption] = useState<number>(CHART_DATA_OPTION.ACTIVITY);
    const [isChartCollapsed, setIsChartCollapsed] = useState(false);

    useEffect(() => {
        if (!instanceDetails?.id || !timeRange) return;

        const fetchData = async () => {
            setPlotLines([]);
            try {
                const [
                    statementsResponse,
                    waitsResponse,
                    clientsResponse,
                    programsResponse,
                    usersResponse,
                    sessionsResponse,
                    databasesResponse,
                    allEventsResponse
                ] = await Promise.all([
                    ActivityApi.getSql(buildApiQuery(timeRange), instanceDetails.id),
                    ActivityApi.getWaitEvents(buildApiQuery(timeRange), instanceDetails.id),
                    ActivityApi.getDimension('client', buildApiQuery(timeRange), instanceDetails.id),
                    ActivityApi.getDimension('program', buildApiQuery(timeRange), instanceDetails.id),
                    ActivityApi.getDimension('username', buildApiQuery(timeRange), instanceDetails.id),
                    ActivityApi.getDimension('session', buildApiQuery(timeRange), instanceDetails.id),
                    ActivityApi.getDimension('database', buildApiQuery(timeRange), instanceDetails.id),
                    EventsApi.getEventsChangesAndAlerts(timeRangeContext, Number(instanceDetails.id))
                ]);

                const waitsWithColor = (waitsResponse.data || []).map((wait: Wait, idx: number) => ({
                    ...wait,
                    color: CHART_COLOURS_WAITS[idx % CHART_COLOURS_WAITS.length]
                }));

                const statementsWithColor = (statementsResponse.data || []).map((statement: Statement, idx: number) => ({
                    ...statement,
                    color: CHART_COLOURS_STATEMENTS[idx % CHART_COLOURS_STATEMENTS.length]
                }));

                setWaits(waitsWithColor);

                setStatements(statementsWithColor || []);
                setWaits(waitsWithColor || []);
                setClients(clientsResponse.data || []);
                setPrograms(programsResponse.data || []);
                setUsers(usersResponse.data || []);
                setSessions(sessionsResponse.data || []);
                setDatabases(databasesResponse.data || []);

                let combinedChanges: CombinedChanges[] = [];
                for (let index = 0; index < allEventsResponse.length; index++) {
                    const currentItem = allEventsResponse[index];
                    combinedChanges.push({
                        id: index,
                        instanceId: currentItem.datasourceid,
                        timeslice: currentItem.timeslice,
                        [currentItem.event]: currentItem
                    });
                }
                combinedChanges.sort((a, b) => Helper.parseTimesliceWithTz(String(b.timeslice)).valueOf() - Helper.parseTimesliceWithTz(String(a.timeslice)).valueOf());

                const interval = Helper.getInterval(timeRange?.from, timeRange?.to, timeRange?.interval);
                const chartCategories = Helper.getChartAxisCategories(timeRange?.from, timeRange?.to, interval);
                const generatedPlotLines = Helper.getChartPlotLines(buildRedirectQuery(timeRangeContext.timeRange), combinedChanges, chartCategories, interval, false, undefined, eventTypes);

                setPlotLines(generatedPlotLines || []);
            } catch (error) {
                console.error('Error fetching chart data for slideout:', error);
                setStatements([]);
                setWaits([]);
                setClients([]);
                setPrograms([]);
                setUsers([]);
                setSessions([]);
                setDatabases([]);
                setPlotLines([]);
            }
        };

        void fetchData();
    }, [instanceDetails?.id, timeRange.from.valueOf(), timeRange.to.valueOf(), timeRange.interval]);

    if (!instanceDetails) {
        return <div className="p-error p-p-3">Instance details not provided.</div>;
    }

    let dbIconClassName = 'icon icon-database';
    let dbIconColor = '';
    const instanceTypeLower = instanceDetails.type?.toLowerCase();
    const instanceVersionLower = instanceDetails.version?.toLowerCase();

    if (instanceTypeLower?.includes('sqlserver')) {
        dbIconClassName = 'icon icon-mssql';
    } else if (instanceTypeLower?.includes('mysql')) {
        dbIconClassName = instanceVersionLower?.includes('mariadb') ? 'icon icon-mariadb' : 'icon icon-mysql-alt';
    } else if (instanceTypeLower?.includes('oracle')) {
        dbIconClassName = 'icon icon-oracle';
    } else if (instanceTypeLower?.includes('postgres')) {
        dbIconClassName = 'icon icon-postgres';
    } else if (instanceTypeLower?.includes('cockroach')) {
        dbIconClassName = 'icon fak fa-cockroachdb';
    } else if (instanceTypeLower?.includes('db2')) {
        dbIconClassName = 'icon fak fa-db2';
        dbIconColor = '#04992B';
    } else if (instanceTypeLower?.includes('informix')) {
        dbIconClassName = 'icon fak fa-informix';
        dbIconColor = '#33348F';
    } else if (instanceTypeLower?.includes('saphana')) {
        dbIconClassName = 'icon fak fa-saphana';
        dbIconColor = '#027FC5';
    } else if (instanceTypeLower?.includes('sybase')) {
        dbIconClassName = 'icon fak fa-sapase';
        dbIconColor = '#027FC5';
    } else if (instanceTypeLower?.includes('mongo')) {
        dbIconClassName = 'icon fak fa-mongodb';
        dbIconColor = '#00684A';
    }

    const typeTooltipTargetClass = `slideout-type-icon-${instanceDetails.id}`;

    return (
            <div className="instance-detail-content pt-3">
                <div className="row justify-content-between align-items-center mb-3 px-3">
                    <div className="col">
                        <div className="header-content d-flex align-items-center">
                        <span className="me-2 flex-shrink-0">
                            <StatusIcon instanceId={instanceDetails.id} mode="view"/>
                        </span>
                            <h5 className="mb-0 me-2 text-truncate">
                                {instanceDetails.name}
                            </h5>
                            <span
                                    className={`instance-type-version-badge-slideout p-1 rounded me-2 flex-shrink-0 ${typeTooltipTargetClass}`}
                                    data-bs-toggle="tooltip"
                                    data-bs-placement="top"
                                    title={instanceDetails.type || 'N/A'}
                            >
                            <i className={`${dbIconClassName} me-1`} style={{ color: dbIconColor }} aria-hidden="true"></i>
                                {instanceDetails.version &&
                                    <span className="instance-version-text">{instanceDetails.version.length > 35 ? instanceDetails.version.substring(0, 35) + '...' : instanceDetails.version}</span>}
                        </span>

                            <span className="flex-shrink-0">
                                <InstanceAgBadge instanceId={instanceDetails.id}/>
                            </span>

                            {instanceDetails.tags && Object.keys(instanceDetails.tags).length > 0 && (
                                    <div className="custom-tags d-inline">
                                        {Object.entries(instanceDetails.tags).map(([key, value]) => (
                                                <span key={key} className="filter-button tag">{key}: {value}</span>
                                        ))}
                                    </div>
                            )}
                        </div>
                    </div>

                    <div className="col-auto">
                        <div className="control-buttons d-flex align-items-center">
                            <Link
                                    to={`/instances/${instanceDetails.id}/activity?fm=${timeRange.from.format(API_DATE_FORMAT)}&to=${timeRange.to.format(API_DATE_FORMAT)}&tz=${timeRange.timezone}&interval=0`}
                                    className="p-button p-button-sm p-button-outlined p-button-secondary me-2 view-instance-button-slideout"
                                    aria-label="View instance details"
                            >
                                <i className="fal fa-database fa-fw me-1"></i>
                                View instance
                            </Link>

                            <button
                                    type="button"
                                    className="btn-close custom-slideout-close-button"
                                    aria-label="Close"
                                    onClick={onClose}
                            ></button>
                        </div>
                    </div>
                </div>

                <div
                        className="header-divider mb-3"
                        style={{ borderBottom: '1px solid #dee2e6', width: '100%', display: 'block' }}
                ></div>

                <div className="row g-2 mb-0 px-3 row-cols-1 row-cols-sm-2 row-cols-xl-4 flexlist">
                    <WidgetCard
                            name="Total Time"
                            type='time'
                            iconClassName="time"
                            icon="fa-stopwatch"
                            value={instanceDetails?.metrics?.totalTime || 0}
                            previous={instanceDetails?.metrics?.totalTimePrevious || 0}
                            cardTooltipText="The total time spent"
                    />

                    <WidgetCard
                            name="Executions"
                            iconClassName="executions"
                            icon="fa-chart-line"
                            value={instanceDetails?.metrics?.totalExecutions || 0}
                            previous={instanceDetails?.metrics?.totalExecutionsPrevious || 0}
                            cardTooltipText="The total number of executions"
                    />

                    <WidgetCard
                            name="Average Time"
                            type='time'
                            icon="fa-clock"
                            iconClassName="averagetime"
                            value={(instanceDetails?.metrics?.totalExecutions || 0) > 0 ? (instanceDetails?.metrics?.totalTime || 0) / (instanceDetails?.metrics?.totalExecutions || 0) : 0}
                            previous={(instanceDetails?.metrics?.totalExecutionsPrevious || 0) > 0 ? (instanceDetails?.metrics?.totalTimePrevious || 0) / (instanceDetails?.metrics?.totalExecutionsPrevious || 0) : undefined}
                            cardTooltipText="The average time per execution"
                    />

                    <WidgetCard
                            name="Events"
                            events={{
                                alerts: instanceDetails.eventCounts?.alert || 0,
                                changes: instanceDetails.eventCounts?.change || 0,
                                custom: instanceDetails.eventCounts?.custom || 0
                            }}
                    />
                </div>

                <div className="row g-2 px-3 mt-n2">
                    <div className="col">
                        <div className="card mb-4">
                            <div className="card-header">
                                <i className="fal fa-chart-bar fa-fw" aria-hidden="true"></i>
                                Time spent
                                <i
                                        className="collapse-toggle"
                                        role="button"
                                        onClick={() => setIsChartCollapsed(!isChartCollapsed)}
                                        data-bs-toggle="collapse"
                                        data-bs-target="#instanceDetailChartCollapse"
                                        aria-expanded={!isChartCollapsed}
                                        aria-controls="instanceDetailChartCollapse"
                                ></i>
                                <div className="btn-group float-end">
                                    <button
                                            id="btnChartList"
                                            type="button"
                                            className="btn btn-xsm btn-primary dropdown-toggle ms-1"
                                            data-bs-toggle="dropdown"
                                            aria-expanded="false"
                                    >
                                        {chartOptionsList.find(opt => opt.value === selectedDataOption)?.label || 'Select Chart'}
                                    </button>
                                    <ul className="dropdown-menu border" aria-labelledby="btnChartList">
                                        {chartOptionsList.map(option => (
                                                <li key={option.value}>
                                            <span
                                                    className={`dropdown-item ${selectedDataOption === option.value ? 'active' : ''}`}
                                                    role="button"
                                                    onClick={() => setSelectedDataOption(option.value)}
                                            >
                                                {option.label}
                                            </span>
                                                </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                            <div id="instanceDetailChartCollapse" className={`collapse ${isChartCollapsed ? '' : 'show'}`}>
                                <div className="card-body px-3">

                                    {statements.length > 0 || waits.length > 0 || plotLines.length > 0 ? (
                                            <TimeChart
                                                    dataOption={selectedDataOption}
                                                    instanceId={instanceDetails.id}
                                                    plotLines={plotLines}
                                                    waits={waits}
                                                    statements={statements}
                                                    clients={clients}
                                                    programs={programs}
                                                    users={users}
                                                    sessions={sessions}
                                                    databases={databases}
                                                   
                                                    batches={[]}
                                                    filters={""}
                                            />
                                    ) : (
                                            <div className="text-center text-muted p-3">
                                                <i className="fal fa-info-circle me-2"></i>
                                                No activity data available for the selected time range.
                                            </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                {hostDetails && (
                        <>
                            <div className="row g-2 px-3 mt-n2">
                                <div className="col">
                                    <ProcessorChart
                                            host={hostDetails}
                                           
                                    />
                                </div>
                            </div>
                            <div className="row g-2 px-3 mt-n2">
                                <div className="col">
                                    <DiskChart host={hostDetails}/>
                                </div>
                            </div>
                        </>
                )}

            </div>
    );
};

export default InstanceDetailSlideout;
